import camelcaseKeys from "camelcase-keys"
import ky, { HTTPError, Options } from "ky"
import snakecaseKeys from "snakecase-keys"
import { ApiValidationError } from "./ApiValidationError"
import { CustomApiError } from "./CustomApiError"
import { BaseAuthStore } from "../types/auth"

export interface ApiClientConfig {
  authStore: BaseAuthStore
  onLogout: () => void
  apiUrl: string
  locale?: string
  retryLimit?: number
  skipRefreshPaths?: string[]
}

export interface LoginResponse {
  data: {
    token: string
    refreshToken: string
  }
}

interface ResponseData {
  error: {
    message: string
    status?: number
  }
  errors?: {
    [key: string]: string[]
  }
}

export function createApiClient(config: ApiClientConfig) {
  const {
    authStore,
    onLogout,
    apiUrl,
    locale = "nl",
    retryLimit = 1,
    skipRefreshPaths = ["/auth/login"],
  } = config

  return ky.create({
    prefixUrl: apiUrl,
    retry: {
      limit: retryLimit,
      methods: ["get", "post", "put", "delete", "patch"],
      statusCodes: [401],
    },
    hooks: {
      beforeRequest: [
        async (request, options: Options) => {
          request.headers.set("x-locale", locale)

          const token = authStore.getToken()
          if (token) {
            request.headers.set("Authorization", `Bearer ${token}`)
          }

          if (options.json && typeof options.json === "object") {
            const snakeCasedData = snakecaseKeys(
              options.json as Record<string, unknown>,
              { parsingOptions: { locale: false } },
            )

            return new Request(request.url, {
              method: request.method,
              headers: request.headers,
              body: JSON.stringify(snakeCasedData),
            })
          }

          if (
            request.body &&
            !request.bodyUsed &&
            request.headers.get("Content-Type") === "application/json"
          ) {
            const json = (await request.json()) as Record<string, unknown>
            const body = JSON.stringify(
              snakecaseKeys(json, { parsingOptions: { locale: false } }),
            )

            return new Request(request, { body })
          }

          return request
        },
      ],
      beforeError: [
        async (error) => {
          if (error.response.status === 422) {
            const responseData = await error.response.json()
            throw new ApiValidationError({
              ...error.response,
              status: error.response.status,
              statusText: error.response.statusText,
              data: responseData as ResponseData,
            })
          } else if (error instanceof HTTPError) {
            const responseData = await error.response.json()
            throw new CustomApiError({
              ...error.response,
              status: error.response.status,
              statusText: error.response.statusText,
              data: responseData as ResponseData,
            })
          }
          throw error
        },
      ],
      beforeRetry: [
        async ({ error, request }) => {
          if (
            error instanceof CustomApiError &&
            error.response.status === 401
          ) {
            const url = new URL(request.url)

            // Skip refresh for certain paths (like 2FA)
            if (skipRefreshPaths.some((path) => url.pathname.includes(path))) {
              throw error
            }

            const refreshToken = authStore.getRefreshToken()

            try {
              const refreshResponse = await ky.post(
                `${apiUrl}/auth/refresh_token`,
                {
                  json: {
                    refresh_token: refreshToken,
                  },
                },
              )

              if (refreshResponse.ok) {
                const {
                  data: { token, refreshToken },
                } = await refreshResponse.json<LoginResponse>()
                authStore.setToken(token)
                authStore.setRefreshToken(refreshToken)
              } else {
                throw new Error("Refresh failed")
              }
            } catch {
              if (onLogoutCallback) {
                onLogoutCallback()
              } else {
                onLogout()
              }
            }
          }
        },
      ],
      afterResponse: [
        async (_input, _options, response) => {
          const clonedResponse = response.clone()
          try {
            const body = (await response.json()) as Record<string, unknown>
            const convertedBody = camelcaseKeys(body, {
              deep: true,
            })

            return new Response(JSON.stringify(convertedBody), clonedResponse)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (e) {
            // If response is not JSON, just return it as is. This happens on 204 No Content responses.
            return clonedResponse
          }
        },
      ],
    },
  })
}

let apiClientInstance: ReturnType<typeof createApiClient> | null = null
let onLogoutCallback: (() => void) | null = null

export function configureApiClient(config: {
  authStore: BaseAuthStore
  onLogout: () => void
  apiUrl: string
}) {
  onLogoutCallback = config.onLogout
  apiClientInstance = createApiClient(config)
}

export function getApiClient() {
  if (!apiClientInstance) {
    throw new Error("API Client not configured")
  }

  return apiClientInstance
}
